// @flow

// Libs
import _filter from 'lodash/filter'
import _get from 'lodash/get'
import _some from 'lodash/some'
import _map from 'lodash/map'
import _includes from 'lodash/includes'
import _uniqueId from 'lodash/uniqueId'
import React, { Component } from 'react'

// Utils
import traverse from 'fc-utils/traverse'

// Types
import type { ImportJobsToolOperation } from 'hfty-operator-import-jobs-tool/types'

// Components
import FormField from '@react-mdc/form-field'
import Dropzone from 'react-dropzone'
import Select from 'react-select'
import CloseIcon from 'fc-icons/src/Close'
import { Errors } from 'hfty-components-errors'

// Localization
import { FormattedMessage } from 'react-intl'
import messages from '../messages'

// Model
import {
  PROP_SEASON_ID,
  PROP_FILES,
  PROP_WARNINGS,
  FILE_EXTENSION,
  validateSeason,
  validateFiles,
} from 'hfty-model-import-jobs-tool'

// Constants
import {
  MSG_SEASON_LABEL,
  MSG_WARNING_WRONG_EXTENSION,
  MSG_WARNING_UNKNOWN,
} from '../constants'

// Styles
import {fields as styles} from './styles'

type Props = {
  importJobsToolOperation: ImportJobsToolOperation,
  updateImportJobsToolOperation: Function,
  seasonOptions: Array<{ value: number, label: string }>,
}

type SelectedOption = {
  value: number | string,
  label: string
}

// Getters
const getSeason = traverse(['model', PROP_SEASON_ID], { default: '' })
const getFiles = traverse(['model', PROP_FILES], { default: [] })
const getWarnings = traverse(['model', PROP_WARNINGS], { default: [] })

export default class ImportJobsFormFields extends Component {
  props: Props

  constructor (props: Props) {
    super(props)

    this.onChangeSeason = this.onChangeSeason.bind(this)
    this.onAddFile = this.onAddFile.bind(this)
    this.onRemoveFile = this.onRemoveFile.bind(this)
  }

  handleOperationUpdate ({ updatedModelProps = {}, formFieldErrors = [], updatedFormField }: Object) {
    const { importJobsToolOperation } = this.props
    const errors = importJobsToolOperation.errors
    const filteredErrors = _filter(errors,
      (error: Object): boolean => !(Object.keys(error).indexOf(updatedFormField) >= 0))
    const updatedErrors = [].concat(filteredErrors, formFieldErrors)

    this.props.updateImportJobsToolOperation({
      ...importJobsToolOperation,
      model: {
        ...importJobsToolOperation.model,
        ...updatedModelProps,
      },
      errors: updatedErrors,
    })
  }

  onChangeSeason (selectedOption: SelectedOption) {
    const selectedSeason = _get(selectedOption, 'value', undefined)
    const updatedModelProps = {
      [PROP_SEASON_ID]: selectedSeason,
    }
    const formFieldErrors = validateSeason(selectedSeason)

    this.handleOperationUpdate({
      updatedModelProps,
      formFieldErrors,
      updatedFormField: PROP_SEASON_ID,
    })
  }

  onAddFile (acceptedFiles: Array<Object>, rejectedFiles: Array<Object>) {
    const files = getFiles(this.props.importJobsToolOperation)
    const filteredAcceptedFiles = _filter(acceptedFiles, (acceptedFile: Object): boolean =>
      !_some(files, (existingFile: Object): boolean =>
        acceptedFile.name === existingFile.name))
    const updatedFilesModel = [].concat(files, filteredAcceptedFiles)
    const formFieldErrors = validateFiles(updatedFilesModel)
    const warnings = this.composeFileWarnings(rejectedFiles)
    const updatedModelProps = {
      [PROP_FILES]: updatedFilesModel,
      warnings,
    }

    this.handleOperationUpdate({
      updatedModelProps,
      formFieldErrors,
      updatedFormField: PROP_FILES,
    })
  }

  composeFileWarnings (rejectedFiles: Array<Object>): Array<Object> {
    return _map(rejectedFiles, (file: Object): Object => {
      let fileWarning = {
        details: file.name,
      }
      const hasExpectedExtension = _includes(file.name, `.${FILE_EXTENSION}`) || _includes(file.type, FILE_EXTENSION)

      if (!hasExpectedExtension) {
        fileWarning.id = MSG_WARNING_WRONG_EXTENSION
      } else {
        fileWarning.id = MSG_WARNING_UNKNOWN
      }

      return fileWarning
    })
  }

  onRemoveFile (removedFile: Object) {
    const files = getFiles(this.props.importJobsToolOperation)
    const updatedFilesModel = _filter(files, (file: Object): boolean => file.name !== removedFile.name)
    const formFieldErrors = validateFiles(updatedFilesModel)
    const updatedModelProps = {
      [PROP_FILES]: updatedFilesModel,
      warnings: [],
    }

    this.handleOperationUpdate({
      updatedModelProps,
      formFieldErrors,
      updatedFormField: PROP_FILES,
    })
  }

  renderFileList (files: Array<Object>): React.Element<any> {
    return (
      <ul>
        {_map(files, (fileData: Object): React.Element<any> => (
          <li key={fileData.name} style={styles.fileInfo}>
            <p style={styles.fileName}>
              {fileData.name}
            </p>
            <span onClick={(evt: Object) => {
              evt.stopPropagation()
              this.onRemoveFile(fileData)
            }}>
              <CloseIcon size='19px' />
            </span>
          </li>
        ))}
      </ul>
    )
  }

  renderWarnings (warnings: Array<Object>): React.Element<any> {
    return (
      <ul style={styles.warningWrapper}>
        {_map(warnings, (warning: Object): React.Element<any> => (
          <li key={_uniqueId()}>
            {warning.details}<FormattedMessage {...messages[MSG_WARNING_WRONG_EXTENSION]} />
          </li>
        ))}
      </ul>
    )
  }

  render (): React.Element<any> {
    const { importJobsToolOperation } = this.props
    const files = getFiles(importJobsToolOperation)
    const hasErrors = importJobsToolOperation && !!importJobsToolOperation.errors.length
    const warnings = getWarnings(importJobsToolOperation)

    return (
      <div style={styles.fieldsWrapper}>
        {hasErrors && <Errors
          errors={importJobsToolOperation.errors}
          messages={messages}
          style={styles.errorList}
        />}
        {!!warnings.length && this.renderWarnings(warnings)}
        <FormField key='season-selector'>
          <label style={styles.label} htmlFor='season-selector'>
            <FormattedMessage {...messages[MSG_SEASON_LABEL]} />
          </label>
          <Select
            name='season-selector'
            placeholder={'Select a season'}
            value={getSeason(this.props.importJobsToolOperation)}
            options={this.props.seasonOptions}
            onChange={this.onChangeSeason}
            style={styles.select}
            className='clear-margin'
          />
        </FormField>
        <Dropzone
          onDrop={this.onAddFile}
          accept='.zip'
          style={styles.dropZone}
        >
          {!files.length && <p style={styles.centredText}>
            <span style={styles.underlinedText}>Select a file</span> or Drag Here (.zip )
          </p>}
          {files.length > 0 && this.renderFileList(files)}
        </Dropzone>
      </div>
    )
  }
}
